import { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { QueryRef, useReadQuery, useSubscription } from "@apollo/client";
import {
  maxSmall,
  primary01,
  shade800,
  shade950,
} from "@songtradr-one/components-frontend";
import { NotificationsPanelQuery, graphql } from "@songtradr-one/shared-common";
import { getApollo } from "@songtradr-one/shared-frontend";
import { staticText } from "../../shared/statics/static-text";
import { NotificationsPanelList_Subscription } from "./notifications-panel-list/NotificationsPanelList.Parts";

// TEXT
export const text = staticText.panel;

// STYLES
export const PanelContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 28.125rem;
  max-height: min(45rem, 80vh);
  overflow: hidden;
  background: ${shade950};
`;

export const PanelHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: 1.5rem;
  background: ${shade950};
  border-bottom: 1px solid ${shade800};
`;

export const MobileSheet = styled.div`
  display: flex;
  flex: 1;
  min-height: 0;
  flex-direction: column;
`;

export const MobileTabs = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem 1.5rem;
`;

export const BellWrapper = styled.div`
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.5rem;
  height: 1.5rem;

  @media ${maxSmall} {
    svg {
      height: 1rem;
    }
  }
`;

export const NotificationDot = styled.span`
  position: absolute;
  top: -0.125rem;
  right: -0.125rem;
  width: 0.5rem;
  height: 0.5rem;
  background: ${primary01};
  border-radius: 100%;
`;

// DATA
export const NotificationsPanel_Query = graphql(`
  query NotificationsPanel {
    notifications {
      unreadCount
    }
  }
`);

// TYPES
export type Props = {
  queryRef: QueryRef<NotificationsPanelQuery, unknown>;
};

export type NotificationsPanelTab = "all" | "unread";

// CONSTANTS

// FUNCTIONS
export const useNotificationsQueryRef = () => {
  const { preloadQuery } = getApollo();
  return useMemo(() => preloadQuery(NotificationsPanel_Query), [preloadQuery]);
};

export const useNotificationsPanel = (
  queryRef: QueryRef<NotificationsPanelQuery, unknown>,
) => {
  const { data } = useReadQuery(queryRef);
  const unread = data.notifications.unreadCount ?? 0;

  const [open, setOpen] = useState(false);

  // Mounted for the whole app session (not just while the dropdown is
  // open), so the count keeps up in the background too. While closed, bump
  // it locally instead of hitting the network. While open, the panel
  // list's own subscription (useNotificationsFeed) already evicts on this
  // same event, so there's nothing to do here.
  useSubscription(NotificationsPanelList_Subscription, {
    onData: ({ client, data: { data, error } }) => {
      if (error || !data?.newNotification || open) return;

      client.cache.modify({
        fields: {
          notifications: (existing, { isReference }) =>
            isReference(existing)
              ? existing
              : {
                  ...existing,
                  unreadCount:
                    ((existing as { unreadCount?: number }).unreadCount ?? 0) +
                    1,
                },
        },
      });
    },
  });

  // Reconciles the count and list with the server on open, since neither
  // is kept live while the panel is closed.
  useEffect(() => {
    if (!open) return;
    const { client } = getApollo();
    client.cache.evict({ fieldName: "notifications" });
    client.cache.gc();
  }, [open]);

  const [activeTab, setActiveTab] = useState<NotificationsPanelTab>("all");

  return {
    unread,
    open,
    setOpen,
    activeTab,
    setActiveTab,
  };
};

// ASSETS
